import React from 'react';
import PropTypes from 'prop-types';
import Hammer from 'react-hammerjs';

import {OEMediaViewerReadyState, OEMediaViewerContentSizeMode} from '../oe-media-viewer-model';
import OEMediaViewerContentController from './oe-media-viewer-content-controller';
import OEResizeObserver from '../../../lib/components/oe-resize-observer';
import OEAnimationLoop from '../../../lib/animation/oe-animation-loop';
import OEColor from '../../color-picker/oe-color';
import OEToolbox from '../../../lib/tools/oe-toolbox';

export default class OEMediaViewerImageContentController extends React.PureComponent {

    constructor(props) {
        super(props);

        this.readyState = OEMediaViewerReadyState.noMeta;
        this.contentSize = null;

        this.size = null;
        this.contentAspectSize = null;

        this.maxZoom = 4;
        this.zoom = {x: 0, y: 0, s: 1};

        this.state = {
            zoom: this.zoom,
            size: null,
            contentSize: null,
            contentAspectSize: null
        };

        this.onGotSize = this.onGotSize.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onRef = this.onRef.bind(this);

        this.onWheel = this.onWheel.bind(this);
        this.onPan = this.onPan.bind(this);
        this.onPinch = this.onPinch.bind(this); 
        this.onDoubleTap = this.onDoubleTap.bind(this);
        this.onMouseLeave = this.onMouseLeave.bind(this);

        this.framemove = this.framemove.bind(this);
    }

    getReadyState() {
        return this.readyState;
    }

    getContentSize()    {
        return this.contentSize;
    }

    /*
    componentWillUnmount()  {
        this.unregisterListener();
    }
    */

    componentWillReceiveProps(nextProps) {
        if(nextProps.src !== this.props.src)     {
            if(this.readyState !== OEMediaViewerReadyState.noMeta) {
                this.readyState = OEMediaViewerReadyState.noMeta;
                this.props.onReadyStateChanged(this.readyState);
            }
        }
    }

    updateContentAspectSize()   {
        if(!this.size || !this.contentSize || !this.size.h || !this.contentSize.h) {
            this.contentAspectSize = null;
            this.setState({contentAspectSize: this.contentAspectSize});
            return;
        }

        this.contentAspectSize = {w: this.size.w, h: this.size.h};
        let aspect = this.size.w / this.size.h;
        let contentAspect = this.contentSize.w / this.contentSize.h;

        if(contentAspect > aspect)  {
            this.contentAspectSize.h = this.contentAspectSize.w / contentAspect;
        } else {
            this.contentAspectSize.w = this.contentAspectSize.h * contentAspect;
        }

        this.setState({contentAspectSize: this.contentAspectSize});
    }

    constrainedZoom(z, f)   {
        let delta = Math.min(Math.max(typeof(f) === 'number' ? f : 1, 0), 1);

        let zoom = {x: z.x, y: z.y, s: z.s};
        let size = this.size;
        let contentAspectSize = this.contentAspectSize;

        if(!size || !contentAspectSize)    return zoom;

        let zSize = {w: zoom.s * contentAspectSize.w, h: zoom.s * contentAspectSize.h};

        let pos = {x: zoom.x, y: zoom.y};

        if(zoom.s <= 1) {
            pos.x = 0.5 * (size.w - zSize.w);
            pos.y = 0.5 * (size.h - zSize.h);
        } else {
            if(zSize.w > size.w)    {
                if(size.w > zSize.w + pos.x)    {
                    pos.x = size.w - zSize.w;
                } else if(pos.x > 0)    {
                    pos.x = 0;
                }
            } else {
                if(size.w < pos.x + zSize.w)    {
                    pos.x = size.w - zSize.w;
                } else if(pos.x < 0)    {
                    pos.x = 0;
                }
            }

            if(zSize.h > size.h)    {
                if(size.h > zSize.h + pos.y)    {
                    pos.y = size.h - zSize.h;
                } else if(pos.y > 0)    {
                    pos.y = 0;
                }
            } else {
                if(size.h < pos.y + zSize.h)    {
                    pos.y = size.h - zSize.h;
                } else if(pos.y < 0)    {
                    pos.y = 0;
                }
            }
        }

        let dis = {x: (pos.x - zoom.x), y: (pos.y - zoom.y)};

        zoom.x += delta * dis.x;
        zoom.y += delta * dis.y;

        return zoom;
    }

    applyConstraints(f)  {
        let zoom = this.constrainedZoom(this.zoom, f);
    
        if(!OEToolbox.shallowEqual(zoom, this.zoom))   {
            this.zoom = zoom;
            this.setState({zoom: this.zoom});
        }
    }

    getRelativeCenter() {
        if(!this.size || !this.contentAspectSize || !this.size.w || !this.size.h) return;
        let zSize = {w: this.zoom.s * this.contentAspectSize.w, h: this.zoom.s * this.contentAspectSize.h};
        return {x: (this.zoom.x + 0.5 * zSize.w) / this.size.w, y: (this.zoom.y + 0.5 * zSize.h) / this.size.h };
    }

    setRelativeCenter(center) {
        if(!center || !this.size || !this.contentAspectSize) return;

        let zSize = {w: this.zoom.s * this.contentAspectSize.w, h: this.zoom.s * this.contentAspectSize.h};

        let zoom = {x: center.x * this.size.w - 0.5 * zSize.w, y: center.y * this.size.h - 0.5 * zSize.h, s: this.zoom.s};

        if(!OEToolbox.shallowEqual(zoom, this.zoom))   {
            this.zoom = zoom;
            this.setState({zoom: this.zoom});
        }
    }

    updateMaxZoom() {
        if(!this.size || !this.contentSize || !this.size.w || !this.size.h) {
            this.maxZoom = 4;
            return;
        }

        this.maxZoom = 4 * Math.max(Math.max(this.contentSize.w / this.size.w, this.contentSize.h / this.size.h), 1);
    }

    canSwipe()  {
        return this.zoom.s <= 1;
    }

    setZoom(zoom, animated, duration) {

        if(!animated && !OEToolbox.shallowEqual(zoom, this.zoom))   {
            this.zoom = {x: zoom.x, y: zoom.y, s: zoom.s};
            this.setState({zoom: this.zoom});
            this.animateZoom = null;
        } else {
            this.animateZoom = {
                start: {x: this.zoom.x, y: this.zoom.y, s: this.zoom.s},
                final: {x: zoom.x, y: zoom.y, s: zoom.s},
                duration: duration || 0.333,
                time: 0
            };
        }
    }

    isAnimatingZoom()   {
        return this.animateZoom != null;
    }

    applyScale(scale, pos, animated, duration)  {
        if(scale === this.zoom.s)   return;
        let pos_ = pos || (this.size ? {x: 0.5 * this.size.w, y: 0.5 * this.size.h} : {x: 0, y: 0});
        let s = scale / this.zoom.s;
        let zoom = {x: s * this.zoom.x + pos_.x * (1 - s), y: s * this.zoom.y + pos_.y * (1 - s), s: scale};
        this.setZoom(zoom, animated, duration);
    }

    onGotSize(size, src)    {
        let center = this.getRelativeCenter();
        
        let oldSize = this.contentSize;

        this.contentSize = size;
        this.setState({contentSize: this.contentSize});
        this.updateMaxZoom();
        this.updateContentAspectSize();
        this.setRelativeCenter(center);
        this.applyConstraints();

        if(OEToolbox.shallowEqual(this.contentSize, oldSize)) return;

        this.props.onContentSizeChanged(size);
    }

    onResize(sender, size)  {
        this.sizeObserver = sender;

        let center = this.getRelativeCenter();

        this.size = size;
        this.setState({size: size});
        this.updateMaxZoom();
        this.updateContentAspectSize();
        this.setRelativeCenter(center);
        this.applyConstraints();
    }

    onLoad(event)   {
        if(this.readyState !== OEMediaViewerReadyState.ready) {
            this.readyState = OEMediaViewerReadyState.ready;
            this.props.onReadyStateChanged(this.readyState);
        }

        this.onGotSize({w: event.target.naturalWidth, h: event.target.naturalHeight}, event.target.currentSrc);
    }

    onError(event)   {
        if(this.readyState !== OEMediaViewerReadyState.error) {
            this.readyState = OEMediaViewerReadyState.error;
            this.props.onReadyStateChanged(this.readyState);
        }
    }

    registerListener()  {
        if(!this.ref)   return;

        if(!this.eventListener) {
            this.eventListener = {};

            this.eventListener.onLoad = function(e) {
                if(this.ref !== e.target)  return;
                //console.log('Image load', e);
                this.onLoad(e);
            }.bind(this);

            this.eventListener.onError = function(e) {
                if(this.ref !== e.target)  return;
                //console.log('Image error', e);
                this.onError(e);
            }.bind(this);
        }

        this.ref.addEventListener('load', this.eventListener.onLoad, false);
        this.ref.addEventListener('error', this.eventListener.onError, false);
    }

    unregisterListener()    {
        if(!this.ref || !this.eventListener)   return;

        this.ref.removeEventListener('load', this.eventListener.onLoad, false);
        this.ref.removeEventListener('error', this.eventListener.onError, false);
    }

    onRef(ref) {
        if(this.ref === ref)    return;
        this.unregisterListener();
        this.ref = ref;
        this.registerListener();
    }

    render()    {
        let contentRectStyle;

        if(this.props.contentRect)  {
            if(this.props.contentRect.mode === OEMediaViewerContentSizeMode.fill || this.props.fullscreen)   {
                contentRectStyle = {backgroundColor: OEColor.toDOMStr(this.props.backgroundColor), width: '100%', height: '100%'};
            } else {
                contentRectStyle = {
                    backgroundColor: OEColor.toDOMStr(this.props.backgroundColor),
                    top: this.props.contentRect.top,
                    left: this.props.contentRect.left,
                    width: this.props.contentRect.w,
                    height: this.props.contentRect.h
                };
            }

        } else {
            contentRectStyle = {backgroundColor: 'rgba(0,0,0,0)'};
        }

        let zoomStyle;
        if(!this.state.contentAspectSize)   {
            zoomStyle = {width: '100%', height: '100%'};
        } else {
            zoomStyle = {
                top: this.state.zoom.y,
                left: this.state.zoom.x,
                width: this.state.zoom.s * this.state.contentAspectSize.w,
                height: this.state.zoom.s * this.state.contentAspectSize.h
            };
        }

        return (
            <OEMediaViewerContentController
                className={this.props.className}
            >

                <Hammer
                    onPanStart={this.onPan} onPan={this.onPan} onPanEnd={this.onPan} onPanCancel={this.onPan}
                    onPinchStart={this.onPinch} onPinch={this.onPinch} onPinchEnd={this.onPinch} onPinchCancel={this.onPinch}
                    onDoubleTap={this.onDoubleTap}
                    direction="DIRECTION_ALL"
                    options={{
                        recognizers: {
                           pinch: { enable: true }
                        }
                    }}
                >

                    <div
                        className="zoom-container"
                        style={contentRectStyle}
                        onWheel={this.onWheel}
                        onMouseLeave={this.onMouseLeave}
                    >

                        <OEAnimationLoop onFramemove={this.framemove} />

                        <OEResizeObserver onResize={this.onResize} />

                        <img
                            src={this.props.src}
                            style={zoomStyle}
                            ref={this.onRef}
                        />

                    </div>

                </Hammer>

            </OEMediaViewerContentController>
        );
    }

    onWheel(event)  {
        if(!this.sizeObserver || this.isAnimatingZoom()) return;
        let refPos = this.sizeObserver.getClientPos();
        if(!refPos) return;

        //console.log('onWheel - deltaY: ' + event.deltaY.toString() + ' deltaMode: ' + event.deltaMode.toString());
        let wheelParams = OEToolbox.normalizedWheelParams(event);

        let pos = {x: event.clientX - refPos.x, y: event.clientY - refPos.y};
        let s = 1 + -0.001*wheelParams.deltaY;
        if(s === 1) return;

        let newS = this.zoom.s * s;

        if(newS < 1 && this.zoom.s >= 1)    {
            newS = 1;
        }

        this.applyScale(newS, pos);
    }

    onPan(event)   {
        if(event.type === 'panstart')   {
            this.isPan = true;
            this.pan = {deltaX: 0, deltaY: 0};
            return;
        }

        if(event.type === 'panend' || event.type === 'pancancel')   {
            this.isPan = false;
            return;
        }

        if(!this.isPan) return;

        let delta = {x: event.deltaX - this.pan.deltaX, y: event.deltaY - this.pan.deltaY};
        this.pan = {deltaX: event.deltaX, deltaY: event.deltaY};

        if(this.isAnimatingZoom()) return;

        this.vel = {x: 1000 * event.velocityX, y: 1000 * event.velocityY};

        if(this.zoom.s <= 1 || (delta.x === 0 && delta.y === 0)) return;

        //console.log('pan - deltaX: ' + delta.x.toString() + ' deltaY: ' + delta.y.toString());
        let zoom = {x: this.zoom.x + delta.x, y: this.zoom.y + delta.y, s: this.zoom.s};
        this.zoom = zoom;
        this.setState({zoom: this.zoom});
    }

    onPinch(event)    {
        if(event.type === 'pinchstart')   {
            this.isPinch = true;
            this.pinch = {scale: 1};
            return;
        }

        if(event.type === 'pinchend' || event.type === 'pinchcancel')   {
            this.isPinch = false;
            return;
        }

        if(!this.isPinch) return;

        let delta = event.scale / this.pinch.scale;
        this.pinch = {scale: event.scale};

        if(this.isAnimatingZoom() || !this.sizeObserver || !this.sizeObserver.getClientPos()) return;

        let refPos = this.sizeObserver.getClientPos();
        let pos = {x: event.center.x - refPos.x, y: event.center.y - refPos.y};

        this.applyScale(this.zoom.s * delta, pos);
    }

    onDoubleTap(event)  {
        if(this.zoom.s > 1)    {
            let pos = this.size && this.contentAspectSize ? {x: 0.5 * (this.size.w - this.contentAspectSize.w), y: 0.5 * (this.size.h - this.contentAspectSize.h)} : {x: 0, y: 0};
            this.setZoom({x: pos.x, y: pos.y, s: 1}, true, 0.333);
        } else if(this.sizeObserver && this.sizeObserver.getClientPos()) {
            let refPos = this.sizeObserver.getClientPos();
            let pos = {x: event.center.x - refPos.x, y: event.center.y - refPos.y};
            this.applyScale(this.maxZoom / 4, pos, true, 0.333);
        }
    }

    onMouseLeave()  {
        this.isPan = false;
    }

    framemove(time, deltaTime)  {
        // damp scroll velocity
        if(this.vel)    {
            let damp = Math.max(1 - 5.0*deltaTime, 0);
            this.vel.x *= damp;
            this.vel.y *= damp;
        }
        
        // apply zoom animation
        if(this.isAnimatingZoom())  {
            this.animateZoom.time += deltaTime;
            let zoom = {x: this.animateZoom.final.x, y: this.animateZoom.final.y, s: this.animateZoom.final.s};

            if(this.animateZoom.time < this.animateZoom.duration)   {
                let frac = OEToolbox.easeTiming(this.animateZoom.time / this.animateZoom.duration);
                zoom = {
                    x: (1 - frac) * this.animateZoom.start.x + frac * this.animateZoom.final.x,
                    y: (1 - frac) * this.animateZoom.start.y + frac * this.animateZoom.final.y,
                    s: (1 - frac) * this.animateZoom.start.s + frac * this.animateZoom.final.s
                } ;
            } else {
                this.animateZoom = null;
            }

            if(!OEToolbox.shallowEqual(zoom, this.zoom))   {
                this.zoom = zoom;
                this.setState({zoom: this.zoom});
            }

            this.vel = null;

            return;
        }

        // min max zoom constraints
        let s = this.zoom.s;

        if(s < 1)   {
            s = Math.min(s + 4.0 * Math.max((1 - s), 1) * deltaTime, 1);
        }

        if(s > this.maxZoom)   {
            s = Math.max(s + 4.0 * Math.min((this.maxZoom - s), -1) * deltaTime, this.maxZoom);
        }

        if(s !== this.zoom.s)    {
            this.applyScale(s);
        }

        // scroll with repsect to velocity and delta time
        let zoom = {x: this.zoom.x, y: this.zoom.y, s: this.zoom.s};
        
        if(zoom.s > 1 && !this.isPan && !this.pinch && this.vel) {
            zoom.x += deltaTime * this.vel.x;
            zoom.y += deltaTime * this.vel.y;
        }

        // apply scroll constraints
        zoom = this.constrainedZoom(zoom, Math.min(10.0 * deltaTime, 1));

        //
        if(!OEToolbox.shallowEqual(zoom, this.zoom))   {
            this.zoom = zoom;
            this.setState({zoom: this.zoom});
        }
    }
}

OEMediaViewerImageContentController.defaultProps = {
    className: '',
    onReadyStateChanged: (state) => {},
    onContentSizeChanged: (size) => {}
};

OEMediaViewerImageContentController.propTypes = {
    className: PropTypes.string,
    onReadyStateChanged: PropTypes.func,
    onContentSizeChanged: PropTypes.func
};